/**
 * Печатные поля: то, что растрируется.
 *
 * Здесь ничего не растекается — здесь печатают. Это единственное семейство
 * с неорганическим характером, и оно нужно ровно ради контраста
 * с остальными: без него «случайный механизм» превращается в «случайный
 * оттенок одного и того же».
 *
 * Приятное совпадение: пороговая матрица растра И ЕСТЬ поле времени
 * прибытия. Порядок, в котором точки «включаются» с ростом тона, —
 * то же самое, что порядок раскрытия. Полиграфия пришла к этой конструкции
 * за полвека до нас.
 */

import { boxBlur, fbm, mix, normalize } from '../core/noise'
import type { FieldFn } from './types'

/**
 * Матрица Байера 16×16 — 256 уровней.
 *
 * Строится замкнутой формулой на битовых операциях: значение — это
 * перемешанные и обращённые биты координат. Рекурсия дала бы то же самое,
 * но её пришлось бы объяснять; здесь достаточно знать, что соседние
 * по значению ячейки максимально далеки друг от друга — потому решётка
 * и не сбивается в комки.
 */
function bayerMatrix(order: number): Float32Array {
  const size = 1 << order
  const m = new Float32Array(size * size)

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      let v = 0
      let bit = 0
      const xc = x ^ y
      const yc = y
      for (let i = 0; i < order; i++) {
        v |= ((yc >> i) & 1) << bit++
        v |= ((xc >> i) & 1) << bit++
      }
      m[y * size + x] = v / (size * size)
    }
  }
  return m
}

/**
 * Упорядоченный дизеринг.
 *
 * Сухое типографское растворение: пиксели гаснут по решётке, глаз читает
 * сетку. Самое дешёвое, что здесь есть, — три операции на пиксель.
 *
 * Доля подмешивания крупного поля — единственная ручка, и она проводит
 * механизм от «чистая решётка» до «еле заметное зерно поверх облака».
 */
export const bayer: FieldFn = ({ w, h, rnd, scale }) => {
  const order = 3
  const size = 1 << order
  const weight = 0.24 + rnd() * 0.2
  const m = bayerMatrix(order)

  // Шаг решётки задан в ЭКРАННЫХ пикселях и переведён в пиксели поля.
  // Без этого пересчёта ячейка растёт вместе с увеличением маски,
  // и вместо сухого дизеринга получается крупная клетка поверх текста.
  const cellPx = Math.max(1, (2.5 + rnd() * 1.5) * scale)

  const grid = new Float32Array(w * h)
  for (let y = 0; y < h; y++) {
    const gy = Math.floor(y / cellPx) % size
    for (let x = 0; x < w; x++) {
      grid[y * w + x] = m[gy * size + (Math.floor(x / cellPx) % size)]!
    }
  }

  const large = fbm(w, h, rnd, { octaves: 2, cells: 2, gain: 0.5 })
  // Решётка не размывается: в ней весь смысл приёма. Размывается основа,
  // по которой она ползёт.
  return normalize(mix(boxBlur(large, w, h, 1), grid, weight))
}

/**
 * Автотипный растр: растущая точка.
 *
 * Не растворение, а НАБУХАНИЕ И СЛИЯНИЕ. Момент, когда точки смыкаются
 * примерно на половине тона и негатив переворачивается в позитив, —
 * визуальный щелчок, которого нет ни у одного порогового механизма.
 *
 * Углы взяты из печати: чёрный ставят под 45°, потому что решётка под этим
 * углом наименее заметна глазу. Разброс небольшой — при заметном отклонении
 * растр начинает читаться как перекос вёрстки.
 */
export const halftone: FieldFn = ({ w, h, rnd, scale }) => {
  const angle = (45 + (rnd() * 16 - 8)) * (Math.PI / 180)
  const cos = Math.cos(angle)
  const sin = Math.sin(angle)
  // Ячейка растра — величина физическая: на экране она должна быть
  // примерно 7-11 пикселей независимо от того, при каком разрешении
  // печётся лист.
  const cell = Math.max(2, (7 + rnd() * 4) * scale)
  const elliptic = rnd() > 0.5
  const squash = elliptic ? 0.62 : 1

  const dots = new Float32Array(w * h)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const u = (x * cos - y * sin) / cell
      const v = (x * sin + y * cos) / cell
      const cx = u - Math.floor(u) - 0.5
      const cy = v - Math.floor(v) - 0.5
      // Радиус нормируем к половине ячейки: площадь идёт как квадрат,
      // но выравнивать шаг здесь не нужно — этим занимаются квантили.
      dots[y * w + x] = Math.min(1, Math.hypot(cx, cy * squash) / 0.5)
    }
  }

  // Крупное поле решает, где растр набухнет раньше. Без него точки растут
  // синхронно по всему листу и приём выглядит машинным.
  const sweep = boxBlur(fbm(w, h, rnd, { octaves: 2, cells: 2, gain: 0.5 }), w, h, 1)
  return normalize(mix(dots, sweep, 0.42))
}

/**
 * Штриховка.
 *
 * Изображение не проявляется и не растворяется, а РИСУЕТСЯ: штрих ложится
 * на штрих. Поле — минимум по нескольким наборам линий под разными углами,
 * и толщина линии, растущая с порогом, даёт растущую маску сама собой.
 *
 * Монотонность здесь не наша находка: у Praun и соавторов (SIGGRAPH 2001)
 * это свойство называется stroke nesting и означает ровно то же самое —
 * штрихи каждого уровня тона входят во все более тёмные.
 */
export const hatch: FieldFn = ({ w, h, rnd, scale }) => {
  const sets = 2 + Math.floor(rnd() * 2)
  const base = rnd() * Math.PI
  const lines = Array.from({ length: sets }, (_, i) => {
    const a = base + (i * Math.PI) / sets + (rnd() * 0.3 - 0.15)
    return {
      cos: Math.cos(a),
      sin: Math.sin(a),
      // Шаг штриха — тоже экранная величина: 6-11 пикселей между линиями.
      freq: 1 / Math.max(2, (6 + rnd() * 5) * scale),
      phase: rnd(),
      // Наборы появляются не разом: сначала редкая штриховка, потом контрштрих.
      delay: i === 0 ? 0 : 0.12 + rnd() * 0.24,
    }
  })

  const out = new Float32Array(w * h)
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let best = Infinity
      for (const l of lines) {
        const u = (x * l.cos - y * l.sin) * l.freq + l.phase
        const d = Math.abs((u - Math.floor(u)) * 2 - 1)
        const t = l.delay + d * (1 - l.delay)
        if (t < best) best = t
      }
      out[y * w + x] = best
    }
  }

  // Лёгкая неровность снимает машинность: настоящее перо не держит шаг.
  const wobble = boxBlur(fbm(w, h, rnd, { octaves: 2, cells: 3, gain: 0.5 }), w, h, 1)
  return normalize(mix(normalize(out), wobble, 0.14))
}
